import { MutationOptions, QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Message } from 'ably';
import { AxiosError } from 'axios';
import { useContext, useEffect, useRef, useState } from 'react';

import { scheduleExpectationsChannelName } from '@/channels/utils/channelNames';
import {
  GET_SCHEDULE_EXPECTATION_END_CYCLE_NODES_QUERY_KEY,
  GET_SCHEDULE_EXPECTATION_QUERY_KEY,
  GET_SEQUENCE_END_CYCLE_NODES_QUERY_KEY,
  LIST_ACTIVE_CUSTOM_SCHEDULE_EXPECTATIONS_QUERY_KEY,
  LIST_ALL_SCHEDULE_EXPECTATION_CYCLES_QUERY_KEY,
  LIST_CHRONOLOGICAL_APPOINTMENT_TYPES_QUERY_KEY,
  LIST_CHRONOLOGICAL_SCHEDULE_EXPECTATIONS_QUERY_KEY,
  LIST_SCHEDULE_EXPECTATION_CYCLES_QUERY_KEY
} from '@/constants/scheduleExpectations';
import AppContext from '@/contexts/appContext';
import type { CustomScheduleExpectationFormPayload } from '@/forms/CustomScheduleExpectationForm/types';
import useAPI from '@/hooks/useAPI';
import { useChannelData } from '@/hooks/useChannel';
import ScheduleExpectationRepository from '@/repositories/ScheduleExpectationRepository';
import type { AppointmentType } from '@/types/appointmentType';
import type {
  ChronologicalScheduleExpectation,
  CustomScheduleExpectation,
  CustomScheduleExpectationSequence,
  FutureAppointmentRequestForDatePayload,
  MedicalStarterSequencePayload,
  ScheduleExpectation,
  ScheduleExpectationCycle,
  StartNodes
} from '@/types/scheduleExpectation';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

// ---------------------------------------------------------------------------
// Internal helper — invalidates the three query groups touched by every
// schedule-expectation mutation. Call before any mutation-specific extras.
// ---------------------------------------------------------------------------
export const invalidateCycleQueries = async (queryClient: QueryClient, phaseId: string) => {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: [LIST_SCHEDULE_EXPECTATION_CYCLES_QUERY_KEY, phaseId]
    }),
    queryClient.invalidateQueries({
      queryKey: [LIST_ALL_SCHEDULE_EXPECTATION_CYCLES_QUERY_KEY, phaseId]
    }),
    // Prefix-match: invalidates all [LIST_CHRONOLOGICAL_SCHEDULE_EXPECTATIONS_QUERY_KEY, ...]
    queryClient.invalidateQueries({
      queryKey: [LIST_CHRONOLOGICAL_SCHEDULE_EXPECTATIONS_QUERY_KEY]
    }),
    queryClient.invalidateQueries({
      queryKey: [LIST_CHRONOLOGICAL_APPOINTMENT_TYPES_QUERY_KEY, phaseId]
    }),
    queryClient.invalidateQueries({
      queryKey: [LIST_ACTIVE_CUSTOM_SCHEDULE_EXPECTATIONS_QUERY_KEY, phaseId]
    })
  ]);

  // Remove cached default schedule expectation queries to clear any stale 404 errors.
  // Without this, a previously cached error persists even when the query becomes disabled,
  // and useAPI's useEffect re-surfaces it.
  queryClient.removeQueries({
    queryKey: [GET_SCHEDULE_EXPECTATION_QUERY_KEY, phaseId]
  });
};

export const useFetchNextScheduledAppointmentForType = ({
  phaseId,
  appointmentTypeHealthieId,
  enabled = true
}: {
  phaseId: string;
  appointmentTypeHealthieId: string;
  enabled?: boolean;
}) => {
  const repository = ScheduleExpectationRepository();

  return useAPI<ScheduleExpectationCycle | null>({
    queryKey: ['getNextScheduledCycle', phaseId, appointmentTypeHealthieId],
    queryFn: async () => {
      return await repository.getNextScheduledCycle({ phaseId, appointmentTypeHealthieId });
    },
    enabled: enabled && !!phaseId && !!appointmentTypeHealthieId
  });
};

export const useDefaultScheduleExpectation = ({
  phaseId,
  appointmentTypeId,
  enabled = true,
  supressErrorMessages = false
}: {
  phaseId: string;
  appointmentTypeId?: string;
  enabled?: boolean;
  supressErrorMessages?: boolean;
}) => {
  const repository = ScheduleExpectationRepository();

  return useAPI({
    queryKey: [GET_SCHEDULE_EXPECTATION_QUERY_KEY, phaseId, appointmentTypeId],
    queryFn: async () => {
      return await repository.getScheduleExpectation({
        phaseId,
        appointmentTypeId
      });
    },
    enabled,
    supressErrorMessages
  });
};

export const useEndCustomScheduleExpectation = (
  phaseId: string,
  options?: MutationOptions<ScheduleExpectation, AxiosError, { data: { end_at: string } }>
) => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const scheduleExpectationRepository = ScheduleExpectationRepository();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { end_at: string } }) =>
      scheduleExpectationRepository.updateCustomScheduleExpectation({ id, data }),
    onSuccess: async () => {
      addMessage({ level: 'success', text: 'Schedule expectation ended successfully' });
      await invalidateCycleQueries(queryClient, phaseId);
    },
    onError: (error) => {
      addMessage(mapAxiosErrorMessages(error));
    },
    ...options
  });
};

export const useDeleteCustomScheduleExpectation = (
  phaseId: string,
  options?: MutationOptions<void, AxiosError, { id: string }>
) => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const scheduleExpectationRepository = ScheduleExpectationRepository();

  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      scheduleExpectationRepository.deleteCustomScheduleExpectation({ id }),
    onSuccess: async () => {
      addMessage({ level: 'success', text: 'Schedule expectation deleted successfully' });
      await invalidateCycleQueries(queryClient, phaseId);
    },
    ...options
  });
};

export const useCustomScheduleExpectationEndCycleNodes = (
  customScheduleExpectationId: string,
  enabled = true
) => {
  const repository = ScheduleExpectationRepository();

  return useAPI({
    enabled,
    queryKey: [GET_SCHEDULE_EXPECTATION_END_CYCLE_NODES_QUERY_KEY, customScheduleExpectationId],
    queryFn: async () => {
      return await repository.getScheduleExpectationEndCycleNodes({ customScheduleExpectationId });
    }
  });
};

export const useChronologicalScheduleExpectations = ({
  phaseId,
  appointmentTypeId,
  enabled = true
}: {
  phaseId: string;
  appointmentTypeId: string;
  enabled?: boolean;
}) => {
  const repository = ScheduleExpectationRepository();

  const isEnabled = Boolean(phaseId) && Boolean(appointmentTypeId) && enabled;
  return useAPI<ChronologicalScheduleExpectation[]>({
    queryKey: [LIST_CHRONOLOGICAL_SCHEDULE_EXPECTATIONS_QUERY_KEY, phaseId, appointmentTypeId],
    queryFn: async () => {
      return await repository.listChronologicalScheduleExpectations({
        phaseId,
        appointmentTypeId
      });
    },
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000 // 5 minutes - keep data fresh to avoid refetching on tab switch
  });
};

export const useChronologicalAppointmentTypes = ({
  phaseId,
  enabled = true
}: {
  phaseId: string;
  enabled?: boolean;
}) => {
  const repository = ScheduleExpectationRepository();

  const isEnabled = Boolean(phaseId) && enabled;
  return useAPI<AppointmentType[]>({
    queryKey: [LIST_CHRONOLOGICAL_APPOINTMENT_TYPES_QUERY_KEY, phaseId],
    queryFn: async () => {
      return await repository.listChronologicalAppointmentTypes({ phaseId });
    },
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000
  });
};

export const useActiveCustomScheduleExpectations = ({
  phaseId,
  enabled = true
}: {
  phaseId: string;
  enabled?: boolean;
}) => {
  const repository = ScheduleExpectationRepository();

  const isEnabled = Boolean(phaseId) && enabled;
  return useAPI<CustomScheduleExpectation[]>({
    queryKey: [LIST_ACTIVE_CUSTOM_SCHEDULE_EXPECTATIONS_QUERY_KEY, phaseId],
    queryFn: async () => {
      return await repository.listActiveCustomScheduleExpectations({ phaseId });
    },
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000
  });
};

export const useCreateCustomScheduleExpectation = (phaseId: string) => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const repository = ScheduleExpectationRepository();

  return useMutation({
    mutationFn: ({ values }: { values: CustomScheduleExpectationFormPayload }) =>
      repository.createCustomScheduleExpectation({ data: values }),
    onSuccess: async () => {
      addMessage({ level: 'success', text: 'Successfully created custom schedule expectation' });
      await invalidateCycleQueries(queryClient, phaseId);
    },
    onError: (error) => {
      addMessage(mapAxiosErrorMessages(error as AxiosError));
    }
  });
};

export const useCreateFutureAppointmentRequestForDate = (phaseId: string) => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const repository = ScheduleExpectationRepository();

  return useMutation({
    mutationFn: ({ data }: { data: FutureAppointmentRequestForDatePayload }) =>
      repository.createFutureAppointmentRequestForDate({ data }),
    onSuccess: async () => {
      addMessage({ level: 'success', text: 'Future appointment request created successfully' });
      await invalidateCycleQueries(queryClient, phaseId);
    },
    onError: (error) => {
      addMessage(mapAxiosErrorMessages(error as AxiosError));
    }
  });
};

export const useSequenceEndCycleNodes = (sequenceId: string, enabled = true) => {
  const repository = ScheduleExpectationRepository();

  return useAPI({
    enabled,
    queryKey: [GET_SEQUENCE_END_CYCLE_NODES_QUERY_KEY, sequenceId],
    queryFn: async () => {
      return await repository.getSequenceEndCycleNodes({ sequenceId });
    }
  });
};

export const useEndSequence = (
  phaseId: string,
  options?: MutationOptions<
    CustomScheduleExpectationSequence,
    AxiosError,
    { id: string; data: { end_at: string } }
  >
) => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const repository = ScheduleExpectationRepository();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { end_at: string } }) =>
      repository.endSequence({ id, data }),
    onSuccess: async () => {
      addMessage({ level: 'success', text: 'Schedule expectation sequence ended successfully' });
      await invalidateCycleQueries(queryClient, phaseId);
    },
    onError: (error) => {
      addMessage(mapAxiosErrorMessages(error));
    },
    ...options
  });
};

export const useDeleteSequence = (
  phaseId: string,
  options?: MutationOptions<void, AxiosError, { id: string }>
) => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const repository = ScheduleExpectationRepository();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => repository.deleteSequence({ id }),
    onSuccess: async () => {
      addMessage({
        level: 'success',
        text: 'Schedule expectation sequence deleted successfully'
      });
      await invalidateCycleQueries(queryClient, phaseId);
    },
    ...options
  });
};

export const useCustomExpectationStartNodes = ({
  phaseId,
  appointmentTypeId,
  enabled = true
}: {
  phaseId: string;
  appointmentTypeId: string;
  enabled?: boolean;
}) => {
  const repository = ScheduleExpectationRepository();

  const isEnabled = Boolean(phaseId) && Boolean(appointmentTypeId) && enabled;
  return useAPI<StartNodes>({
    queryKey: ['getCustomExpectationStartNodes', phaseId, appointmentTypeId],
    queryFn: async () => {
      return await repository.getCustomExpectationStartNodes({ phaseId, appointmentTypeId });
    },
    enabled: isEnabled
  });
};

export const useMedicalStarterStartNodes = ({
  phaseId,
  enabled = true
}: {
  phaseId: string;
  enabled?: boolean;
}) => {
  const repository = ScheduleExpectationRepository();

  return useAPI<StartNodes>({
    queryKey: ['getMedicalStarterStartNodes', phaseId],
    queryFn: async () => {
      return await repository.getMedicalStarterStartNodes({ phaseId });
    },
    enabled: Boolean(phaseId) && enabled
  });
};

export const useCreateMedicalStarterSequence = (phaseId: string) => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const repository = ScheduleExpectationRepository();

  return useMutation<ScheduleExpectation, AxiosError, { data: MedicalStarterSequencePayload }>({
    mutationFn: ({ data }) => repository.createMedicalStarterSequence({ data }),
    onSuccess: async () => {
      addMessage({ level: 'success', text: 'Medical starter sequence created successfully' });
      await invalidateCycleQueries(queryClient, phaseId);
    },
    onError: (error) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
};

export const useScheduleExpectationsCyclesWebsocket = (personId: string) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'idle' | 'queued' | 'processing' | 'complete'>('idle');
  const [appointmentTypeIds, setAppointmentTypeIds] = useState<string[] | null>(null);
  const completeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (completeTimerRef.current) clearTimeout(completeTimerRef.current);
    };
  }, []);

  useChannelData({
    channelId: scheduleExpectationsChannelName(personId),
    channelName: 'schedule expectations current cycles',
    messageCallback: (message: Message) => {
      if (message.name === 'schedule_expectation_current_cycles') {
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [LIST_SCHEDULE_EXPECTATION_CYCLES_QUERY_KEY] }),
          queryClient.invalidateQueries({
            queryKey: [LIST_ALL_SCHEDULE_EXPECTATION_CYCLES_QUERY_KEY]
          }),
          queryClient.invalidateQueries({
            queryKey: [LIST_CHRONOLOGICAL_SCHEDULE_EXPECTATIONS_QUERY_KEY]
          }),
          queryClient.invalidateQueries({
            queryKey: [LIST_CHRONOLOGICAL_APPOINTMENT_TYPES_QUERY_KEY]
          }),
          queryClient.invalidateQueries({
            queryKey: [LIST_ACTIVE_CUSTOM_SCHEDULE_EXPECTATIONS_QUERY_KEY]
          })
        ]);
      } else if (message.name === 'schedule_expectations_queued') {
        const data = JSON.parse(message.data);
        setStatus('queued');
        setAppointmentTypeIds(data.appointment_type_ids ?? null);
        if (completeTimerRef.current) clearTimeout(completeTimerRef.current);
      } else if (message.name === 'schedule_expectations_processing') {
        const data = JSON.parse(message.data);
        setStatus('processing');
        setAppointmentTypeIds(data.appointment_type_ids ?? null);
        if (completeTimerRef.current) clearTimeout(completeTimerRef.current);
      } else if (message.name === 'schedule_expectations_complete') {
        const data = JSON.parse(message.data);
        setStatus('complete');
        setAppointmentTypeIds(data.appointment_type_ids ?? null);
        if (completeTimerRef.current) clearTimeout(completeTimerRef.current);
        completeTimerRef.current = setTimeout(() => {
          setStatus('idle');
          setAppointmentTypeIds(null);
        }, 3000);
      }
    }
  });

  return { status, appointmentTypeIds };
};

export default {};
